// Console profiler for the whole renderer. The renderer installs it with installPerfProfiler(); then, in the browser console:
//
//   await prof.frame()      where a frame's time goes: every section of the frame loop, on the GPU and on the CPU
//   await prof.groups()     what each subsystem costs: switched off and on in turn, inside the running scene (a table)
//   await prof.draws()      GPU time per draw group (grass, sand chunks, terrain, trees, each shadow pass), split into fixed and per-pixel
//   await prof.fill()       how much of the frame scales with the number of pixels (fill-bound) and how much does not
//   prof.scene()            what is in the scene: draw calls, triangles and instances per object, lights, shader programs
//   await prof.sky()        the sky pass alone, against a plain fill of the screen
//   await prof.all()        frame, fill, groups and scene in one go
//   prof.help()   prof.json()   (the last results as text, to paste into a chat)
//
// How it measures
//   * GPU time comes from EXT_disjoint_timer_query_webgl2 (Chrome on desktop has it). Timer queries cannot nest, so the frame is
//     cut into sequential spans: the renderer calls mark('name') after each section of its frame loop, and the shadow-map pass
//     inside renderer.render is cut out by a wrapper on renderer.shadowMap.render. Without the extension the GPU columns are
//     blank, and groups() and fill() fall back to "synced" frame time: begin of frame to the end of a one-pixel read, which
//     includes the CPU and stalls the pipeline, but is not capped by vsync as the frame interval is.
//   * The hooks are inactive (a function call and one branch) until a measurement starts. Nothing is wrapped but
//     renderer.shadowMap.render, and that is put back by dispose().
//   * groups() works in the live scene. For each subsystem it runs blocks of frames with it on, then off, then on, then off
//     (A B A B), drops the first frames of each block (shaders may recompile), and reports the difference of the pair means with its
//     standard error. A control row switches nothing, which shows how large the noise is: a difference inside it is not a result.
//     A spiky cost (a shadow cascade redrawn a few times a second) shows up as a mean, not a median, so means are used.
//   * The adaptive quality controller is held for the duration, and the frame loop must be running (a hidden tab does not draw).
//
//   * Per-draw timing (draws(), and variants() sets that trace) puts a query round every draw. On some GPUs that reads a long fragment-bound draw well below
//     what it costs inside the frame, so the report compares the draws' sum with the colour pass and says when they do not agree; the cloud volume's
//     own pass is a section of the frame loop ('cloud volume (small)') and variants() can read sections and the whole frame instead of draws.
//
// Reading it: "GPU" is GPU time on the timer; "CPU" is the main-thread time of the frame loop (JavaScript plus driver calls).
// If GPU total is near the frame interval the frame is GPU-bound; if CPU total is, it is CPU-bound; if both are well under it,
// the display's refresh is the limit and the difference is headroom.
import * as THREE from 'three';
import type { Sky } from './sky';
import { GpuTimer, sleep, median, pct, mean, sd, sum, maxOf, fmt, signed } from './profCommon';
import { createSkyRunner, SKY_VARIANT_NOTES, type SkyRunOptions } from './skyProfile';

/** A subsystem that can be switched off and on while the scene runs. */
export interface ProfGroup {
  name: string;
  note?: string;
  off(): void;
  on(): void;
  /** Called once when the run is over, after the last on(): puts back whatever on() changed only for the measurement. */
  after?(): void;
  /** Extra frames to drop after a switch (a shader recompile, a cache refill). */
  warmFrames?: number;
  /** The group has no direct effect on the picture (it feeds other things), so "changes no pixels" is not a finding. */
  noPixels?: boolean;
}

export interface PerfContext {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  sky: Sky;
  /** The renderer's own subsystems, beside the two built in (sky pass, sky probe). */
  groups?: ProfGroup[];
  /** Names for objects in the scene, for prof.scene(); anything under one counts toward it. */
  names?: Array<[string, THREE.Object3D]>;
  /** Called when a measurement starts; pauses the adaptive quality controller and returns the function that puts it back. */
  hold?: () => () => void;
  /** Extra lines for the report header (quality level, sand chunk counts, anything that explains the numbers). */
  extra?: () => string[];
  /** Cheaper versions of one draw group's shader or detail, applied one at a time in the live scene to see what each part costs. One set, or several
   *  (each set has its own table in the report). */
  variants?: PerfVariants | PerfVariants[];
}

/** One set of variants: what is changed, and which GPU times say what it saved. */
export interface PerfVariants {
  title: string;
  /** `metrics` name the GPU times that are read: prefixes of the labels of the recorded spans, or the special prefix '=frame' for the whole frame's GPU time.
   *  With `trace` (the default) every draw call is a span of its own, named as in prof.draws; without it the spans are the frame loop's own sections
   *  (the labels in prof.frame), which is the one to use for work that is not a single draw (a pass into a target) and for the whole frame. Per-draw timing
   *  runs each draw alone and, on some GPUs, reads long fragment-bound draws well under what the same draw costs inside the frame (the report says when the
   *  draws do not add up to the frame): there a metric of '=frame' or of a section is the one to believe. */
  metrics: Array<{ name: string; prefixes: string[] }>;
  trace?: boolean;
  list: Array<{ name: string; apply: () => void }>;
  reset: () => void;
}

/** What the frame loop calls. All cheap and inactive unless a measurement is running. */
export interface PerfHooks {
  /** First thing in the frame loop. */
  begin(): void;
  /** After a section: the time (CPU and GPU) since the previous mark, or since begin(), is that section's. */
  mark(name: string): void;
  /** Last thing in the frame loop. */
  end(): void;
  dispose(): void;
}

/** A group that hides objects by emptying their layer mask (the object and everything under it), for things whose own code keeps setting .visible. */
export function layerGroup(name: string, roots: THREE.Object3D[], note?: string): ProfGroup {
  const saved = new Map<THREE.Object3D, number>();
  return {
    name, note,
    off() { for (const r of roots) r.traverse(o => { if (!saved.has(o)) { saved.set(o, o.layers.mask); o.layers.mask = 0; } }); },
    on() { for (const [o, m] of saved) o.layers.mask = m; saved.clear(); },
  };
}

interface Seg { span: string | null; label: string; q: WebGLQuery | null; cpu: number; gpu: number; calls?: number; tris?: number }
interface Rec {
  segs: Seg[]; cpu: number; gap: number; synced: number; calls: number; tris: number;
  shadowCalls: number; shadowTris: number; gpu: number; pix: Uint8Array | null;
}
interface Cap { sections: boolean; synced: boolean; pixelsLast: boolean; want: number; recs: Rec[]; done: (r: Rec[]) => void; kick: () => void }

/** Share of pixels whose colour differs by more than 10/255 in some channel. */
function changedFraction(a: Uint8Array | null, b: Uint8Array | null): number {
  if (!a || !b || a.length !== b.length) return NaN;
  let n = 0;
  for (let i = 0; i < a.length; i += 4) {
    const d = Math.max(Math.abs(a[i] - b[i]), Math.abs(a[i + 1] - b[i + 1]), Math.abs(a[i + 2] - b[i + 2]));
    if (d > 10) n++;
  }
  return n / (a.length / 4);
}

export function installPerfProfiler(ctx: PerfContext): PerfHooks {
  const { renderer, scene, camera, sky } = ctx;
  const gl = renderer.getContext() as WebGL2RenderingContext;
  const timer = new GpuTimer(gl);
  const gpu = timer.available;
  const px = new Uint8Array(4);
  let last: Record<string, unknown> = {};
  let lock: string | null = null;

  /** Pause the adaptive quality controller; the returned function puts it back (once). */
  const holdQuality = () => {
    const f = ctx.hold?.();
    let done = false;
    return () => { if (!done) { done = true; f?.(); } };
  };

  const acquire = (what: string) => {
    if (lock) throw new Error(`prof: ${lock} is still running`);
    lock = what;
    return () => { lock = null; };
  };

  // ── Recording: the hooks the frame loop calls ───────────────────────────────────────────────────────────────────────────
  let cap: Cap | null = null;
  let frame: Rec | null = null;
  let activeQ: WebGLQuery | null = null;
  let lastT = 0, frameT0 = 0, lastBegin = 0, labelFrom = 0;
  const spanStack: string[] = [];
  let framesDone = 0;
  const waiters: Array<{ at: number; resolve: () => void }> = [];

  const startQ = () => { if (gpu) activeQ = timer.start(); };
  const cut = () => {
    if (!frame) return;
    const t = performance.now();
    const seg: Seg = { span: spanStack.length ? spanStack.join(' > ') : null, label: '', q: null, cpu: t - lastT, gpu: NaN };
    lastT = t;
    if (activeQ) { timer.stop(); seg.q = activeQ; activeQ = null; }
    frame.segs.push(seg);
  };
  const relabel = (name: string) => {
    if (!frame) return;
    for (let i = labelFrom; i < frame.segs.length; i++) { const s = frame.segs[i]; s.label = s.span ? `${name} > ${s.span}` : name; }
    labelFrom = frame.segs.length;
  };
  const abortFrame = () => {
    if (activeQ) { timer.stop(); timer.discard(activeQ); activeQ = null; }
    frame = null; spanStack.length = 0;
  };

  let nBegin = 0, nEnd = 0;
  const begin = () => {
    nBegin++;
    if (!cap) return;
    const t = performance.now();
    frame = { segs: [], cpu: 0, gap: lastBegin ? t - lastBegin : NaN, synced: NaN, calls: 0, tris: 0, shadowCalls: 0, shadowTris: 0, gpu: NaN, pix: null };
    lastBegin = t; frameT0 = t; lastT = t; labelFrom = 0; spanStack.length = 0;
    startQ();
  };
  const mark = (name: string) => {
    if (!frame || !cap || !cap.sections) return;
    cut(); relabel(name); startQ();
  };
  const end = () => {
    nEnd++;
    framesDone++;
    for (let i = waiters.length - 1; i >= 0; i--) if (framesDone >= waiters[i].at) { waiters[i].resolve(); waiters.splice(i, 1); }
    if (!frame || !cap) return;
    cut(); relabel(cap.sections ? 'other' : 'frame');
    const rec = frame; frame = null;
    const t = performance.now();
    rec.cpu = t - frameT0;
    if (!gpu && cap.synced) { gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px); rec.synced = performance.now() - frameT0; }
    rec.calls = renderer.info.render.calls; rec.tris = renderer.info.render.triangles;
    if (cap.pixelsLast && cap.recs.length === cap.want - 1) {         // the last frame of the block: keep the picture, to see what a switch changed
      const w = gl.drawingBufferWidth, h = gl.drawingBufferHeight;
      rec.pix = new Uint8Array(w * h * 4);
      gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, rec.pix);
    }
    cap.recs.push(rec);
    cap.kick();
    if (cap.recs.length >= cap.want) { const c = cap; cap = null; c.done(c.recs); }
  };

  // The shadow-map pass is cut out of whatever section it runs in (the main render, or the probe's faces).
  const sm = renderer.shadowMap as any;
  const origShadowRender = sm.render;
  sm.render = function (this: unknown, ...a: unknown[]) {
    if (!frame || !cap || !cap.sections) return origShadowRender.apply(this, a);
    const c0 = renderer.info.render.calls, t0 = renderer.info.render.triangles;
    cut(); spanStack.push('shadow map'); startQ();
    try { return origShadowRender.apply(this, a); }
    finally {
      if (frame) {
        cut(); spanStack.pop(); startQ();
        frame.shadowCalls += renderer.info.render.calls - c0; frame.shadowTris += renderer.info.render.triangles - t0;
      }
    }
  };

  // Why frames might not arrive: the browser stops animation frames for a hidden or covered tab (then the run pauses and resumes by
  // itself when the tab is drawn again), or the frame loop throws before it reaches end() (then the page error is quoted).
  const pageErrors: string[] = [];
  const onErr = (e: ErrorEvent) => { pageErrors.push(`${e.message}${e.filename ? ' @' + e.filename.split('/').pop() + ':' + e.lineno : ''}`); if (pageErrors.length > 5) pageErrors.shift(); };
  const onRej = (e: PromiseRejectionEvent) => { pageErrors.push('rejected: ' + String((e.reason && e.reason.message) || e.reason)); if (pageErrors.length > 5) pageErrors.shift(); };
  window.addEventListener('error', onErr); window.addEventListener('unhandledrejection', onRej);
  const rafAlive = () => new Promise<boolean>(res => {
    let fin = false;
    const t = setTimeout(() => { if (!fin) { fin = true; res(false); } }, 1500);
    requestAnimationFrame(() => { if (!fin) { fin = true; clearTimeout(t); res(true); } });
  });
  const diagnose = () => `begin() ran ${nBegin} times and end() ${nEnd} times since the profiler was installed; tab ${document.visibilityState}, focus ${document.hasFocus()}; page errors: ${pageErrors.length ? pageErrors.slice(-3).join(' | ') : 'none'}`;
  /** Called when no frame has completed for 20 s. true: the tab was not drawing and is again (carry on); false: the frame loop is really not completing. */
  const stalled = async (): Promise<boolean> => {
    if (await rafAlive()) return false;
    const was = statusText;
    setStatus('PAUSED: this tab is not drawing frames (hidden or covered by another window). Bring it to the front; the profile carries on by itself.');
    const t0 = performance.now();
    while (performance.now() - t0 < 900000) { await sleep(500); if (await rafAlive()) { await sleep(500); setStatus(was); return true; } }
    return false;
  };

  // Per-draw timing (prof.draws): every draw call is its own timed span, named after what it draws.
  let drawTrace = false;
  const ownerOf = (obj: THREE.Object3D): string | null => {
    const names = ctx.names ?? [];
    for (let p: THREE.Object3D | null = obj; p; p = p.parent) for (const [n, root] of names) if (root === p) return n;
    for (let p: THREE.Object3D | null = obj; p && !(p as THREE.Scene).isScene; p = p.parent) if (p.name) return p.name;   // fall back to the object's own name
    return null;
  };
  const labelCache = new WeakMap<THREE.Object3D, string>();
  const protoKey = (THREE.Material.prototype as any).customProgramCacheKey;
  const drawLabel = (object: THREE.Object3D): string => {
    let l = labelCache.get(object);
    if (l) return l;
    const m = object as THREE.Mesh;
    const mat = (Array.isArray(m.material) ? m.material[0] : m.material) as THREE.Material | undefined;     // the object's own material, not the shadow pass's depth material
    const geo = m.geometry as THREE.BufferGeometry | undefined;
    const ck = mat && (mat as any).customProgramCacheKey !== protoKey ? String((mat as any).customProgramCacheKey()).slice(0, 36) : '';
    l = (ownerOf(object) ?? (ck || `${mat?.type ?? object.type}${mat?.name ? ' ' + mat.name : ''}`)) + (geo && (geo as any).isInstancedBufferGeometry ? ' (instanced)' : '');
    labelCache.set(object, l);
    return l;
  };
  const rbd = (renderer as any).renderBufferDirect as (...a: unknown[]) => void;
  (renderer as any).renderBufferDirect = function (this: unknown, ...a: unknown[]) {
    if (!drawTrace || !frame || !cap || !cap.sections) return rbd.apply(this, a);
    const c0 = renderer.info.render.calls, t0 = renderer.info.render.triangles;
    cut(); spanStack.push(drawLabel(a[4] as THREE.Object3D)); startQ();
    try { return rbd.apply(this, a); }
    finally {
      if (frame) {
        cut(); spanStack.pop(); startQ();
        const sg = frame.segs[frame.segs.length - 1];
        sg.calls = renderer.info.render.calls - c0; sg.tris = renderer.info.render.triangles - t0;
      }
    }
  };

  /** Resolves after n more frames of the frame loop. */
  const waitFrames = (n: number) => new Promise<void>((resolve, reject) => {
    let dead: ReturnType<typeof setTimeout>;
    const done = () => { clearTimeout(dead); resolve(); };
    const w = { at: framesDone + n, resolve: done };
    const arm = () => {
      dead = setTimeout(async () => {
        if (await stalled()) { w.at = framesDone + n; arm(); return; }     // was not drawing: count the frames again from here
        const i = waiters.findIndex(w => w.resolve === done);
        if (i >= 0) waiters.splice(i, 1);
        reject(new Error('prof: no frame completed in 20 s. ' + diagnose()));
      }, 20000 + n * 300);
    };
    waiters.push(w);
    arm();
  });

  /** Record the next `frames` frames. sections: cut each frame into sections (else one span for the whole frame). */
  const capture = (frames: number, sections: boolean, synced: boolean, pixelsLast = false) => new Promise<Rec[]>((resolve, reject) => {
    let wd: ReturnType<typeof setTimeout>;
    const arm = () => { clearTimeout(wd); wd = setTimeout(fail, 20000); };
    async function fail() {
      const mine = cap;
      if (await stalled()) {                      // the tab was not drawing: the frames so far span a gap, so start this capture again
        if (cap === mine && mine) { mine.recs.length = 0; abortFrame(); lastBegin = 0; arm(); return; }
      }
      cap = null; abortFrame(); reject(new Error('prof: no frame completed in 20 s. ' + diagnose()));
    }
    cap = {
      sections, synced, pixelsLast, want: frames, recs: [],
      done: r => { clearTimeout(wd); resolve(r); },
      kick: () => arm(),
    };
    arm();
  });

  /** Read the GPU timer queries of recorded frames into seg.gpu and rec.gpu. */
  const resolveRecs = async (recs: Rec[]) => {
    const qs: WebGLQuery[] = [], owners: Seg[] = [];
    for (const r of recs) for (const s of r.segs) if (s.q) { qs.push(s.q); owners.push(s); }
    if (qs.length) { const ms = await timer.resolve(qs); owners.forEach((s, i) => { s.gpu = ms[i]; s.q = null; }); }
    for (const r of recs) r.gpu = gpu ? (r.segs.some(s => Number.isNaN(s.gpu)) ? NaN : sum(r.segs.map(s => s.gpu))) : r.synced;
  };

  const envInfo = () => {
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    const buf = renderer.getDrawingBufferSize(new THREE.Vector2());
    return {
      gpu: dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : 'unknown',
      drawingBuffer: `${buf.x}x${buf.y}`, pixelRatio: renderer.getPixelRatio(),
      timing: gpu ? 'GPU timer query' : 'none (synced wall time of the frame; CPU laps only)', disjointEvents: timer.disjoints,
      three: THREE.REVISION,
    };
  };
  const gpuName = gpu ? 'GPU' : 'frame, synced';

  // ── prof.frame(): sections ─────────────────────────────────────────────────────────────────────────────────────────────
  async function frameRun(o: { frames?: number; print?: boolean } = {}) {
    const release = acquire('frame');
    const undo = holdQuality();
    try {
      timer.reset();
      await waitFrames(3);
      const n = o.frames ?? 120;
      const recs = await capture(n, true, true);
      undo();
      await resolveRecs(recs);

      const labels = new Set<string>();
      for (const r of recs) for (const s of r.segs) labels.add(s.label);
      const per = new Map<string, { gpu: number[]; cpu: number[] }>();
      for (const l of labels) per.set(l, { gpu: [], cpu: [] });
      for (const r of recs) {
        const g = new Map<string, number>(), c = new Map<string, number>();
        for (const s of r.segs) { g.set(s.label, (g.get(s.label) ?? 0) + (Number.isFinite(s.gpu) ? s.gpu : 0)); c.set(s.label, (c.get(s.label) ?? 0) + s.cpu); }
        for (const l of labels) { per.get(l)!.gpu.push(g.get(l) ?? 0); per.get(l)!.cpu.push(c.get(l) ?? 0); }
      }
      const gpuTot = recs.map(r => r.gpu), cpuTot = recs.map(r => r.cpu);
      const gaps = recs.map(r => r.gap).filter(Number.isFinite);
      const gpuMean = mean(gpuTot.filter(Number.isFinite));
      const stat = (a: number[]) => `${fmt(median(a))} | ${fmt(mean(a))} | ${fmt(pct(a, 0.9))} | ${fmt(maxOf(a))}`;
      const rows = [...per.entries()].map(([label, v]) => ({ label, gpuMean: mean(v.gpu), cpuMean: mean(v.cpu), v }))
        .sort((a, b) => (gpu ? b.gpuMean - a.gpuMean : 0) || b.cpuMean - a.cpuMean);
      const table = rows.map(r => {
        const row: Record<string, string> = { section: r.label };
        if (gpu) { row['GPU ms (med | mean | p90 | max)'] = stat(r.v.gpu); row['GPU share'] = fmt(100 * r.gpuMean / gpuMean, 0) + '%'; }
        row['CPU ms (med | mean | p90 | max)'] = stat(r.v.cpu);
        return row;
      });
      const shadowFrames = recs.filter(r => r.shadowCalls > 0);
      const gapMed = median(gaps), period = pct(gaps, 0.05);
      const totals: Record<string, string> = {
        'frames recorded': String(recs.length),
        'frame interval': `median ${fmt(gapMed, 1)} ms (${fmt(1000 / gapMed, 0)} fps), p90 ${fmt(pct(gaps, 0.9), 1)}, p99 ${fmt(pct(gaps, 0.99), 1)}, max ${fmt(maxOf(gaps), 1)}`,
        'CPU, frame loop': `mean ${fmt(mean(cpuTot))} ms, p90 ${fmt(pct(cpuTot, 0.9))}, max ${fmt(maxOf(cpuTot))}`,
      };
      if (gpu) totals['GPU, all sections'] = `mean ${fmt(gpuMean)} ms, p90 ${fmt(pct(gpuTot.filter(Number.isFinite), 0.9))}, max ${fmt(maxOf(gpuTot.filter(Number.isFinite)))}`;
      totals['draw calls | triangles'] = `${fmt(median(recs.map(r => r.calls)), 0)} | ${fmt(median(recs.map(r => r.tris)), 0)}  (main render and shadow passes; median)`;
      totals['shadow cascades redrawn'] = shadowFrames.length
        ? `in ${shadowFrames.length} of ${recs.length} frames; those frames draw ${fmt(median(shadowFrames.map(r => r.shadowCalls)), 0)} calls and ${fmt(median(shadowFrames.map(r => r.shadowTris)), 0)} triangles in the shadow pass`
        : 'none in this window';
      // The slowest frames and what made them slow.
      const worst = recs.map((r, i) => ({ i, t: Math.max(Number.isFinite(r.gpu) ? r.gpu : 0, r.cpu), r })).sort((a, b) => b.t - a.t).slice(0, 3).map(x => {
        const parts = x.r.segs.map(s => ({ l: s.label, ms: Math.max(Number.isFinite(s.gpu) ? s.gpu : 0, s.cpu) })).filter(p => p.ms >= 0.8).sort((a, b) => b.ms - a.ms).slice(0, 4);
        return { frame: `#${x.i}`, 'worst of GPU/CPU ms': fmt(x.t), 'biggest sections': parts.map(p => `${p.l} ${fmt(p.ms, 1)}`).join(', ') };
      });
      const cpuMean = mean(cpuTot);
      let verdict: string;
      if (gpu && gpuMean > 1.15 * gapMed) verdict = `GPU-bound, and the browser is running ahead of it: GPU time is ${fmt(gpuMean, 1)} ms per frame against a ${fmt(gapMed, 1)} ms interval, so frames queue (the true interval is nearer the GPU time).`;
      else if (gpu && gpuMean > 0.85 * gapMed) verdict = `GPU-bound: the GPU is busy ${fmt(100 * gpuMean / gapMed, 0)}% of the frame interval.`;
      else if (cpuMean > 0.85 * gapMed) verdict = `CPU-bound: the frame loop takes ${fmt(100 * cpuMean / gapMed, 0)}% of the frame interval.`;
      else verdict = `Neither is saturated${gpu ? ` (GPU ${fmt(100 * gpuMean / gapMed, 0)}%, CPU ${fmt(100 * cpuMean / gapMed, 0)}% of the interval)` : ''}: ${gapMed > 1.3 * period ? 'the interval is long for another reason (the browser, a stall, another tab)' : 'the display refresh is the limit; the rest is headroom'}.`;

      const result = { kind: 'frame', env: envInfo(), totals, verdict, num: { gap: gapMed, period, gpuMean, gpuP90: pct(gpuTot.filter(Number.isFinite), 0.9), cpuMean, cpuP90: pct(cpuTot, 0.9), cpuMax: maxOf(cpuTot), shadowFrames: shadowFrames.length, frames: recs.length, calls: median(recs.map(r => r.calls)), tris: median(recs.map(r => r.tris)) }, sections: rows.map(r => ({ section: r.label, gpuMean: r.gpuMean, cpuMean: r.cpuMean, gpuMed: median(r.v.gpu), cpuMed: median(r.v.cpu), gpuP90: pct(r.v.gpu, 0.9), cpuP90: pct(r.v.cpu, 0.9), cpuMax: maxOf(r.v.cpu) })), worst };
      last = { ...last, frame: result };
      if (o.print !== false) {
        console.log(`%cprof.frame  ${result.env.gpu}  ${result.env.drawingBuffer} @${result.env.pixelRatio}x  timing: ${result.env.timing}`, 'font-weight:bold');
        console.table(table);
        console.table(totals);
        console.log('worst frames:'); console.table(worst);
        console.log('%c' + verdict, 'font-weight:bold');
        console.log('Sections are what the frame loop marks, in order. "x > shadow map" is the shadow pass inside x. A section that only submits work shows its GPU cost under the section it was submitted in.');
        if (timer.disjoints) console.warn(`prof: the GPU reported ${timer.disjoints} disjoint event(s) (clock change); run again if the figures look odd.`);
      }
      return result;
    } finally { cap = null; abortFrame(); undo(); release(); }
  }

  // ── prof.groups(): each subsystem switched off and on ───────────────────────────────────────────────────────────────────
  const builtIn: ProfGroup[] = [
    layerGroup('sky pass (main view)', [sky.mesh], 'the sky pass drawn to the screen; the probe still draws it'),
    {
      name: 'sky probe (6 faces)', note: 'the cube map of the sky from the eye; the app redraws it about once a second, so this measures it drawn every frame (its real cost is that divided by the frames between redraws)', noPixels: true,
      off() { (sky as any).updateProbe = () => {}; }, on() { delete (sky as any).updateProbe; (sky as any).probePeriodOverride = 0; },
      after() { delete (sky as any).updateProbe; (sky as any).probePeriodOverride = null; },
    },
  ];

  async function groupsRun(o: { frames?: number; reps?: number; skip?: number; only?: string[]; print?: boolean } = {}) {
    const release = acquire('groups');
    const undo = holdQuality();
    const frames = o.frames ?? 20, reps = o.reps ?? 3, skip = o.skip ?? 5;
    const all = [...builtIn, ...(ctx.groups ?? [])];
    const chosen = o.only ? all.filter(g => o.only!.includes(g.name)) : all;
    const control: ProfGroup = { name: '(control: nothing switched)', note: 'noise floor: the same procedure with no change', on() {}, off() {} };
    const list = [...chosen, control];
    const metric = {
      gpu: (r: Rec) => r.gpu, cpu: (r: Rec) => r.cpu, calls: (r: Rec) => r.calls, tris: (r: Rec) => r.tris,
    };
    type K = keyof typeof metric;
    const out: Array<{ name: string; note: string; d: Record<K, { v: number; se: number }>; base: Record<K, number>; pix: number }> = [];
    try {
      timer.reset();
      await waitFrames(3);
      for (const g of list) {
        const A: Rec[][] = [], B: Rec[][] = [];
        try {
          for (let r = 0; r < reps; r++) {
            g.on(); await waitFrames(skip + (g.warmFrames ?? 0)); A.push(await capture(frames, false, true, true));
            g.off(); await waitFrames(skip + (g.warmFrames ?? 0)); B.push(await capture(frames, false, true, true));
          }
        } finally { g.on(); g.after?.(); }
        await resolveRecs([...A.flat(), ...B.flat()]);
        const d = {} as Record<K, { v: number; se: number }>, base = {} as Record<K, number>;
        for (const k of Object.keys(metric) as K[]) {
          const diffs = A.map((a, i) => mean(a.map(metric[k]).filter(Number.isFinite)) - mean(B[i].map(metric[k]).filter(Number.isFinite)));
          d[k] = { v: mean(diffs), se: sd(diffs) / Math.sqrt(diffs.length) };
          base[k] = mean(A.flat().map(metric[k]).filter(Number.isFinite));
        }
        const fr = A.map((a, i) => changedFraction(a[a.length - 1].pix, B[i][B[i].length - 1].pix)).filter(Number.isFinite);
        for (const r of [...A.flat(), ...B.flat()]) r.pix = null;
        out.push({ name: g.name, note: g.note ?? '', d, base, pix: mean(fr) });
        await waitFrames(skip + (g.warmFrames ?? 0));     // let the restored state settle before the next group
      }
    } finally {
      for (const g of chosen) { g.on(); g.after?.(); }
      toggled.clear();
      undo(); cap = null; abortFrame(); release();
    }

    const ctl = out[out.length - 1];
    const noise = Math.max(Math.abs(ctl.d.gpu.v), 2 * ctl.d.gpu.se, 0.05);
    const rows = out.slice(0, -1).sort((a, b) => (b.d.gpu.v || 0) - (a.d.gpu.v || 0));
    const fmtD = (x: { v: number; se: number }, dd = 2) => `${signed(x.v, dd)} ± ${fmt(x.se, dd)}`;
    const table = [...rows, ctl].map(r => {
      const sig = r === ctl ? '' : (Math.abs(r.d.gpu.v) > Math.max(2 * r.d.gpu.se, noise) ? '' : ' (within noise)');
      return {
        group: r.name,
        [`${gpuName} ms saved by switching off`]: fmtD(r.d.gpu) + sig,
        share: r === ctl ? '' : fmt(100 * r.d.gpu.v / ctl.base.gpu, 0) + '%',
        'CPU ms saved': fmtD(r.d.cpu),
        'draw calls': signed(r.d.calls.v, 0), triangles: signed(r.d.tris.v, 0),
        'pixels changed': fmt(100 * r.pix, 1) + '%',
      };
    });
    const result = { kind: 'groups', env: envInfo(), frames, reps, skip, baseline: { gpu: ctl.base.gpu, cpu: ctl.base.cpu, calls: ctl.base.calls, tris: ctl.base.tris }, noiseGpuMs: noise, rows: [...rows, ctl].map(r => ({ group: r.name, note: r.note, gpu: r.d.gpu, cpu: r.d.cpu, calls: r.d.calls, tris: r.d.tris, pixChanged: r.pix })) };
    last = { ...last, groups: result };
    if (o.print !== false) {
      console.log(`%cprof.groups  ${result.env.gpu}  ${result.env.drawingBuffer}  ${gpuName} baseline ${fmt(ctl.base.gpu)} ms, CPU ${fmt(ctl.base.cpu)} ms, ${fmt(ctl.base.calls, 0)} draw calls`, 'font-weight:bold');
      console.table(table);
      console.log(`Each figure: frame time with the group on minus off, mean of ${reps} A/B pairs of ${frames} frames (± standard error). Positive = the group costs that much. The control row shows the noise (about ${fmt(noise)} ms here). "pixels changed" is the share of the picture that differs visibly when the group is off; a group that changes about as much as the control draws nothing you can see.${gpu ? '' : ' No GPU timer: the figure is the synced frame time, which includes the CPU.'}`);
      console.table(Object.fromEntries(rows.map(r => [r.name, r.note])));
      if (timer.disjoints) console.warn(`prof: the GPU reported ${timer.disjoints} disjoint event(s) (clock change); run again if the figures look odd.`);
    }
    return result;
  }

  // ── prof.draws(): GPU time of every draw call, grouped by what is drawn ─────────────────────────────────────────────────
  // Switching a subsystem off and comparing frames (prof.groups) is noisy when the GPU changes its clock. This times each draw
  // call by itself instead, in the real scene, in the colour pass and in every shadow pass, at two resolutions, so each object's
  // cost splits into a fixed part (vertices, draw calls) and a part that scales with pixels.
  async function drawsRun(o: { frames?: number; ratios?: number[]; print?: boolean } = {}) {
    if (!gpu) throw new Error('prof.draws needs the GPU timer extension, which this browser does not offer');
    const release = acquire('draws');
    const undo = holdQuality();
    const frames = o.frames ?? 18, ratios = o.ratios ?? [1, 0.35];
    const base = renderer.getPixelRatio();
    type Cell = { ms: number; draws: number; tris: number };
    const pts: Array<{ mpx: number; cells: Map<string, Cell>; frameMs: number; n: number }> = [];
    try {
      timer.reset();
      await waitFrames(3);
      for (const r of ratios) {
        renderer.setPixelRatio(base * r);
        await waitFrames(6);
        const size = renderer.getDrawingBufferSize(new THREE.Vector2());
        const cells = new Map<string, Cell>();
        let frameMs = 0, n = 0;
        for (let b = 0; b < frames; b += 6) {                 // small batches: one timer query per draw call is a lot of queries
          drawTrace = true;
          let recs: Rec[];
          try { recs = await capture(Math.min(6, frames - b), true, false); } finally { drawTrace = false; }
          await resolveRecs(recs);
          for (const rec of recs) {
            n++; let fsum = 0;
            for (const s of rec.segs) {
              const ms = Number.isFinite(s.gpu) ? s.gpu : 0; fsum += ms;
              const c = cells.get(s.label) ?? { ms: 0, draws: 0, tris: 0 };
              c.ms += ms; c.draws += s.calls ? 1 : 0; c.tris += s.tris ?? 0; cells.set(s.label, c);
            }
            frameMs += fsum;
          }
        }
        pts.push({ mpx: size.x * size.y / 1e6, cells, frameMs: frameMs / Math.max(1, n), n });
      }
    } finally {
      drawTrace = false;
      renderer.setPixelRatio(base);
      undo(); cap = null; abortFrame(); release();
    }
    const labels = new Set<string>(); for (const p of pts) for (const k of p.cells.keys()) labels.add(k);
    const per = (p: typeof pts[number], k: string) => { const c = p.cells.get(k); return c ? { ms: c.ms / p.n, draws: c.draws / p.n, tris: c.tris / p.n } : { ms: 0, draws: 0, tris: 0 }; };
    const rows = [...labels].map(k => {
      const ys = pts.map(p => per(p, k).ms), xs = pts.map(p => p.mpx);
      const b = xs.length > 1 ? (ys[0] - ys[ys.length - 1]) / Math.max(1e-9, xs[0] - xs[xs.length - 1]) : 0;
      const a = ys[0] - b * xs[0];
      return { what: k, ms: ys, draws: per(pts[0], k).draws, tris: per(pts[0], k).tris, fixed: Math.max(0, a), perMpx: b, scales: ys[0] > 0 ? Math.min(100, Math.max(0, 100 * b * xs[0] / ys[0])) : 0 };
    }).filter(r => r.ms[0] >= 0.03).sort((x, y) => y.ms[0] - x.ms[0]);
    const result = { kind: 'draws', env: envInfo(), mpx: pts.map(p => p.mpx), frameSumMs: pts.map(p => p.frameMs), rows, frames };
    last = { ...last, draws: result };
    if (o.print !== false) {
      console.log(`%cprof.draws  ${result.env.gpu}  GPU ms per frame by draw group, at ${result.mpx.map(m => fmt(m, 2) + ' Mpx').join(' and ')}`, 'font-weight:bold');
      console.table(rows.slice(0, 30).map(r => ({ what: r.what, 'draws/frame': fmt(r.draws, 0), 'triangles/frame': fmt(r.tris, 0), ...Object.fromEntries(r.ms.map((v, i) => [`ms @${fmt(pts[i].mpx, 2)} Mpx`, fmt(v)])), 'fixed ms': fmt(r.fixed), 'scales with pixels': fmt(r.scales, 0) + '%' })));
      console.log('Timing every draw separately stops draws overlapping on the GPU, so the sum runs a little over the real frame time. "render" alone is the work between draws.');
    }
    return result;
  }

  /** A draw-label pattern: a plain prefix, or `prefix~text` for a label that starts with the prefix and contains the text (a minified
   *  program name changes between builds, a uniform name in it does not). Shadow-pass labels are left out of the second form. */
  function labelMatches(label: string, p: string): boolean {
    if (p === '=frame') return true;
    const k = p.indexOf('~');
    if (k < 0) return label.startsWith(p);
    const pre = p.slice(0, k), inc = p.slice(k + 1);
    return label.startsWith(pre) && label.includes(inc) && !label.slice(pre.length).startsWith('shadow map');
  }

  // ── prof.variants(): what parts of a draw group's shader cost, in the live scene ─────────────────────────────────────────
  const variantSets = (): PerfVariants[] => (ctx.variants ? (Array.isArray(ctx.variants) ? ctx.variants : [ctx.variants]) : []);

  async function variantsRun(o: { frames?: number; print?: boolean; only?: string[] } = {}) {
    const all = variantSets();
    const sets = o.only ? all.filter(s => o.only!.some(t => s.title.toLowerCase().includes(t.toLowerCase()))) : all;
    if (!sets.length) throw new Error('prof.variants: the renderer defined no variants' + (o.only ? ` matching ${o.only.join(', ')} (sets: ${all.map(s => s.title).join('; ')})` : ''));
    if (!gpu) throw new Error('prof.variants needs the GPU timer extension');
    const release = acquire('variants');
    const undo = holdQuality();
    const frames = o.frames ?? 8;
    const results: Array<{ kind: 'variants'; title: string; metrics: string[]; baseline: number[][]; rows: Array<{ name: string; saved: number[] }>; frames: number }> = [];
    try {
      timer.reset();
      for (const sv of sets) {
        const measure = async (): Promise<number[]> => {
          drawTrace = sv.trace !== false;                                  // per-draw spans, or the frame loop's own sections
          let recs: Rec[];
          try { recs = await capture(frames, true, false); } finally { drawTrace = false; }
          await resolveRecs(recs);
          return sv.metrics.map(m => mean(recs.map(r => sum(r.segs.filter(s => Number.isFinite(s.gpu) && m.prefixes.some(p => labelMatches(s.label, p))).map(s => s.gpu)))));
        };
        const rows: Array<{ name: string; v: number[]; base: number[] }> = [];
        let baseNow: number[] = [];
        try {
          sv.reset(); await waitFrames(6);
          baseNow = await measure();
          for (const variant of sv.list) {
            variant.apply(); await waitFrames(5);
            const v = await measure();
            sv.reset(); await waitFrames(5);
            const after = await measure();
            rows.push({ name: variant.name, v, base: baseNow.map((b, i) => 0.5 * (b + after[i])) });   // the baseline measured on both sides: a drifting clock cancels
            baseNow = after;
          }
        } finally { sv.reset(); }
        results.push({ kind: 'variants', title: sv.title, metrics: sv.metrics.map(m => m.name), baseline: rows.map(r => r.base), rows: rows.map(r => ({ name: r.name, saved: r.v.map((x, i) => r.base[i] - x) })), frames });
      }
    } finally { for (const sv of sets) { try { sv.reset(); } catch { /* the objects may be gone */ } } undo(); cap = null; abortFrame(); release(); }
    last = { ...last, variants: results };
    if (o.print !== false) {
      for (const result of results) {
        console.log(`%cprof.variants  ${result.title}  (GPU ms saved per frame; positive = cheaper)`, 'font-weight:bold');
        console.table(result.rows.map(r => ({ change: r.name, ...Object.fromEntries(result.metrics.map((m, i) => [m, fmt(r.saved[i])])) })));
      }
    }
    return results;
  }

  // ── prof.fill(): resolution scaling ─────────────────────────────────────────────────────────────────────────────────────
  async function fillRun(o: { ratios?: number[]; frames?: number; print?: boolean } = {}) {
    const release = acquire('fill');
    const undo = holdQuality();
    const ratios = o.ratios ?? [1, 0.75, 0.5, 0.35], frames = o.frames ?? 30;
    const base = renderer.getPixelRatio();
    const pts: Array<{ r: number; mpx: number; recs: Rec[] }> = [];
    try {
      timer.reset();
      await waitFrames(3);
      for (const r of [...ratios, ratios[0]]) {          // the first ratio again at the end shows drift
        renderer.setPixelRatio(base * r);
        await waitFrames(6);
        const size = renderer.getDrawingBufferSize(new THREE.Vector2());
        pts.push({ r, mpx: size.x * size.y / 1e6, recs: await capture(frames, true, true) });
      }
    } finally {
      renderer.setPixelRatio(base);
      undo(); cap = null; abortFrame(); release();
    }
    await resolveRecs(pts.flatMap(p => p.recs));
    const drift = pts.pop()!;
    const labelSet = new Set<string>(); for (const p of pts) for (const r of p.recs) for (const s of r.segs) labelSet.add(s.label);
    const val = (recs: Rec[], label: string) => mean(recs.map(r => sum(r.segs.filter(s => s.label === label).map(s => (gpu ? (Number.isFinite(s.gpu) ? s.gpu : 0) : s.cpu)))));
    const fit = (ys: number[], xs: number[]) => {       // y = a + b x, least squares
      const mx = mean(xs), my = mean(ys);
      const b = sum(xs.map((x, i) => (x - mx) * (ys[i] - my))) / Math.max(1e-12, sum(xs.map(x => (x - mx) * (x - mx))));
      return { a: my - b * mx, b };
    };
    const xs = pts.map(p => p.mpx);
    const series: Array<{ label: string; ys: number[] }> = [{ label: gpu ? 'frame, all sections' : 'frame, synced', ys: pts.map(p => mean(p.recs.map(r => r.gpu))) }];
    for (const l of labelSet) series.push({ label: l, ys: pts.map(p => val(p.recs, l)) });
    const table = series.filter(s => Math.max(...s.ys) >= 0.2).map(s => {
      const f = fit(s.ys, xs), full = s.ys[0];
      const row: Record<string, string> = { section: s.label };
      pts.forEach((p, i) => { row[`${fmt(p.mpx, 2)} Mpx`] = fmt(s.ys[i]); });
      row['fixed ms'] = fmt(Math.max(0, f.a)); row['ms per Mpx'] = fmt(f.b);
      row['scales with pixels'] = full > 0 ? fmt(Math.min(100, Math.max(0, 100 * f.b * xs[0] / full)), 0) + '%' : 'n/a';
      return row;
    });
    const dr = mean(drift.recs.map(r => r.gpu)) / series[0].ys[0];
    const result = { kind: 'fill', env: envInfo(), ratios, series: series.map(s => ({ label: s.label, ms: s.ys, fit: fit(s.ys, xs) })), mpx: xs, driftRatio: dr };
    last = { ...last, fill: result };
    if (o.print !== false) {
      console.log(`%cprof.fill  ${result.env.gpu}  pixel ratio ${fmt(base, 2)} x [${ratios.join(', ')}]`, 'font-weight:bold');
      console.table(table);
      console.log('"scales with pixels" is the share of the full-resolution time that falls away in proportion to pixel count (a straight-line fit). High: fill-bound, so resolution and per-pixel shader cost are the levers. Low: the cost is vertices, draw calls, shadow passes or the CPU, and lowering resolution will not help.');
      if (Math.abs(dr - 1) > 0.1) console.warn(`prof.fill: the first resolution measured ${fmt(100 * (dr - 1), 0)}% different when repeated at the end (thermal throttling or a changing scene); treat the fit as rough.`);
    }
    return result;
  }

  // ── prof.scene(): what is in the scene ──────────────────────────────────────────────────────────────────────────────────
  function sceneAudit(o: { print?: boolean } = {}) {
    const owner = ownerOf;
    const lights: THREE.Light[] = [];
    const casters: string[] = [];
    const mats = new Set<THREE.Material>();
    type Acc = { objects: number; draws: number; instances: number; tris: number; shadowTris: number; points: number; mats: Set<string>; hidden: number; notes: Set<string> };
    const acc = new Map<string, Acc>();
    const get = (k: string): Acc => { let a = acc.get(k); if (!a) { a = { objects: 0, draws: 0, instances: 0, tris: 0, shadowTris: 0, points: 0, mats: new Set(), hidden: 0, notes: new Set() }; acc.set(k, a); } return a; };
    const visibleChain = (obj: THREE.Object3D) => { for (let p: THREE.Object3D | null = obj; p; p = p.parent) if (!p.visible) return false; return true; };
    scene.traverse(o => {
      if ((o as THREE.Light).isLight) { lights.push(o as THREE.Light); if ((o as THREE.Light).castShadow) casters.push(`${o.type} ${(o as THREE.Light).intensity.toFixed(2)}`); }
      const m = o as THREE.Mesh;
      const geo = m.geometry as THREE.BufferGeometry | undefined;
      if (!geo || !(m.isMesh || (o as THREE.Points).isPoints || (o as THREE.Line).isLine)) return;
      const mat = Array.isArray(m.material) ? m.material[0] : m.material;
      const key = owner(o) ?? `(unnamed) ${o.type} / ${geo.type} / ${mat?.type ?? '?'}`;
      const a = get(key);
      a.objects++;
      if (!visibleChain(o)) { a.hidden++; return; }
      const posN = geo.getAttribute('position')?.count ?? 0;
      const base = geo.index ? geo.index.count : posN;
      const n = Math.max(0, Math.min(base - geo.drawRange.start, geo.drawRange.count));
      const inst = (m as THREE.InstancedMesh).isInstancedMesh ? (m as THREE.InstancedMesh).count : (geo as THREE.InstancedBufferGeometry).isInstancedBufferGeometry ? (geo as THREE.InstancedBufferGeometry).instanceCount : 1;
      const nInst = Number.isFinite(inst) ? inst : 0;
      a.draws += Array.isArray(m.material) ? Math.max(1, geo.groups.length) : 1;
      a.instances += nInst > 1 || (m as THREE.InstancedMesh).isInstancedMesh ? nInst : 0;
      if ((o as THREE.Points).isPoints) a.points += n * nInst; else {
        const t = (n / 3) * nInst;
        a.tris += t;
        if (m.castShadow) a.shadowTris += t;
      }
      for (const mt of Array.isArray(m.material) ? m.material : [m.material]) { a.mats.add(mt?.type ?? '?'); if (mt) mats.add(mt); }
      if (m.castShadow) a.notes.add('casts shadow');
      if (m.receiveShadow) a.notes.add('receives');
      if (mat && (mat as THREE.Material).transparent) a.notes.add('transparent');
      if (m.frustumCulled === false) a.notes.add('never culled');
    });
    const rows = [...acc.entries()].sort((a, b) => (b[1].tris + b[1].points) - (a[1].tris + a[1].points));
    const cascades = lights.filter(l => l.castShadow && (l as THREE.DirectionalLight).isDirectionalLight).length;
    const totTris = sum(rows.map(r => r[1].tris)), totShadow = sum(rows.map(r => r[1].shadowTris));
    const table = rows.map(([k, a]) => ({
      object: k, 'objects (hidden)': `${a.objects}${a.hidden ? ` (${a.hidden})` : ''}`, 'draw calls': a.draws, instances: a.instances || '',
      triangles: Math.round(a.tris) || '', points: a.points || '', 'shadow-caster tris': Math.round(a.shadowTris) || '', notes: [...a.notes].join(', '),
    }));
    const info = renderer.info;
    const matCount = new Map<string, number>();
    for (const mt of mats) { const k = mt.name || mt.type; matCount.set(k, (matCount.get(k) ?? 0) + 1); }
    const programs = (info.programs ?? []) as any[];
    const summary: Record<string, string> = {
      'scene totals': `${fmt(sum(rows.map(r => r[1].draws)), 0)} draw calls, ${fmt(totTris, 0)} triangles (before culling) over ${rows.length} kinds of object`,
      'last frame (renderer.info)': `${info.render.calls} calls, ${info.render.triangles} triangles, ${info.render.points} points, ${info.render.lines} lines`,
      lights: `${lights.length} (${casters.length} cast shadows: ${casters.join('; ') || 'none'})`,
      'one full shadow redraw': `about ${fmt(totShadow, 0)} caster triangles per directional light with shadows, ${cascades} such lights: ${fmt(totShadow * Math.max(1, cascades), 0)} triangles if all are redrawn (frustum culling per cascade cuts this)`,
      memory: `${info.memory.geometries} geometries, ${info.memory.textures} textures`,
      'materials in view': `${mats.size}: ${[...matCount.entries()].sort((a, b) => b[1] - a[1]).map(([n, c]) => `${n} x${c}`).join(', ')}`,
      'shader programs': `${programs.length} compiled (every light, shadow or fog change recompiles the lit ones; ${programs.length > 40 ? 'that is a lot, check for materials that differ only by a define' : 'a normal number'})`,
    };
    const result = { kind: 'scene', env: envInfo(), summary, rows: table };
    last = { ...last, scene: result };
    if (o.print !== false) {
      console.log('%cprof.scene', 'font-weight:bold');
      console.table(table);
      console.table(summary);
      console.log('Counts are of what is visible in the scene graph, before frustum culling, so they bound the real work above. The frame totals are renderer.info after the last render. Name more objects through the profiler context (names) to fold "(unnamed)" rows.');
    }
    return result;
  }

  const sky_ = createSkyRunner({ renderer, camera, sky }, timer, acquire, envInfo);

  // prof.toggle(): switch a registered group off or on and leave it, to look at the result (the groups' own names, see prof.groupNames).
  const toggled = new Set<string>();
  const toggle = (name?: string, on?: boolean) => {
    const all = [...builtIn, ...(ctx.groups ?? [])];
    if (name === undefined) { console.table(Object.fromEntries(all.map(g => [g.name, toggled.has(g.name) ? 'OFF' : 'on']))); return [...toggled]; }
    const g = all.find(x => x.name === name) ?? all.find(x => x.name.toLowerCase().includes(name.toLowerCase()));
    if (!g) throw new Error(`prof.toggle: no group "${name}". Names: ${all.map(x => x.name).join(', ')}`);
    const wantOn = on ?? toggled.has(g.name);
    if (wantOn) { g.on(); toggled.delete(g.name); } else { g.off(); toggled.add(g.name); }
    console.log(`${g.name}: ${wantOn ? 'on' : 'OFF'}${toggled.size ? `   (off now: ${[...toggled].join(', ')})` : ''}`);
    return [...toggled];
  };

  // ── prof.report(): everything, as one block of text to paste ───────────────────────────────────────────────────────────
  let box: HTMLDivElement | null = null, boxStatus: HTMLDivElement | null = null, boxText: HTMLTextAreaElement | null = null;
  const ensurePanel = () => {
    if (box) return;
    box = document.createElement('div');
    box.style.cssText = 'position:fixed;right:12px;bottom:12px;width:480px;max-width:92vw;z-index:99999;background:rgba(10,14,22,0.95);color:#bfe8d4;font:12px/1.4 monospace;padding:8px;border:1px solid #3f8f6f;border-radius:4px';
    boxStatus = document.createElement('div'); boxStatus.style.marginBottom = '6px';
    boxText = document.createElement('textarea'); boxText.readOnly = true; boxText.rows = 8; boxText.style.cssText = 'width:100%;box-sizing:border-box;background:#06090f;color:#bfe8d4;border:1px solid #2a4a3a;font:11px monospace';
    const bar = document.createElement('div'); bar.style.marginTop = '6px';
    const mk = (label: string, fn: () => void) => { const b = document.createElement('button'); b.textContent = label; b.style.cssText = 'margin-right:8px;font:12px monospace;cursor:pointer'; b.onclick = fn; return b; };
    bar.append(
      mk('Copy', () => { boxText!.select(); void copyText(boxText!.value).then(ok => { boxStatus!.textContent = ok ? 'Copied.' : 'Copy failed: select the text and press Ctrl+C.'; }); }),
      mk('Close', () => { box?.remove(); box = null; }),
    );
    box.append(boxStatus, boxText, bar);
    document.body.appendChild(box);
  };
  let statusText = '';
  const setStatus = (t: string) => { statusText = t; ensurePanel(); boxStatus!.textContent = t; };
  const copyText = async (t: string) => {
    try {
      const ta = document.createElement('textarea'); ta.value = t; ta.style.cssText = 'position:fixed;opacity:0;left:-9999px';
      document.body.appendChild(ta); ta.select(); const ok = document.execCommand('copy'); ta.remove(); if (ok) return true;
    } catch { /* fall through */ }
    try { await navigator.clipboard.writeText(t); return true; } catch { return false; }
  };

  let lastReport = '';
  async function report(o: { quick?: boolean; sky?: boolean } = {}) {
    const quick = !!o.quick, t0 = performance.now();
    const nStages = (o.sky === false ? 4 : 5) + (gpu ? 1 : 0) + (gpu && ctx.variants ? 1 : 0);
    let stageNo = 0;
    const res: Record<string, any> = {}, errs: string[] = [];
    const stage = async (name: string, fn: () => Promise<any> | any) => {
      stageNo++;
      setStatus(`Profiling ${stageNo}/${nStages}: ${name}...  (${Math.round((performance.now() - t0) / 1000)} s so far). Keep this tab in front and leave the camera alone.`);
      try { res[name] = await fn(); } catch (e) { errs.push(`${name}: ${(e as Error).message}`); }
    };
    setStatus('Profiling starts. Keep this tab in front and leave the camera alone; it pauses by itself if the tab is covered.');
    await stage('frame', () => frameRun({ frames: quick ? 30 : 90, print: false }));
    // Block lengths follow the frame rate, so a slow scene does not make the run long: each block is about 0.6 s.
    const gapMs = res.frame?.num?.gap > 0 ? res.frame.num.gap : 50;
    const blk = Math.max(5, Math.min(24, Math.round(600 / gapMs))), skp = Math.max(2, Math.min(5, Math.round(300 / gapMs)));
    await stage('fill', () => fillRun({ frames: quick ? 10 : Math.max(10, Math.min(30, blk + 6)), ratios: quick ? [1, 0.5] : undefined, print: false }));
    if (gpu && ctx.variants) await stage('variants', () => variantsRun({ frames: quick ? 4 : 8, print: false }));
    if (gpu) await stage('draws', () => drawsRun({ frames: quick ? 6 : 18, ratios: quick ? [1, 0.5] : undefined, print: false }));
    await stage('groups', () => groupsRun({ frames: quick ? 6 : blk, reps: quick ? 2 : 3, skip: quick ? 2 : skp, print: false }));
    if (o.sky !== false) await stage('sky', () => sky_({ samples: quick ? 3 : 16, repeat: quick ? 1 : 2, passes: quick ? 1 : 2, views: quick ? ['current'] : undefined, print: false }));
    await stage('scene', () => sceneAudit({ print: false }));
    last = { ...last, ...res };

    // ---- the text ----
    const L: string[] = [];
    const pad = (c: string, n: number) => (c.length >= n ? c : c + ' '.repeat(n - c.length));
    const table = (head: string[], rows: string[][]) => {
      const w = head.map((h, i) => Math.max(h.length, ...rows.map(r => (r[i] ?? '').length)));
      L.push(head.map((h, i) => pad(h, w[i])).join('  ').trimEnd());
      for (const r of rows) L.push(r.map((c, i) => pad(c ?? '', w[i])).join('  ').trimEnd());
    };
    const env = envInfo();
    const dir = camera.getWorldDirection(new THREE.Vector3());
    const pitchDeg = Math.asin(THREE.MathUtils.clamp(dir.y, -1, 1)) * 180 / Math.PI, yawDeg = Math.atan2(dir.x, -dir.z) * 180 / Math.PI;
    const sunUp = ((sky.mesh.material as any)?.uniforms?.uSunUp?.value as number | undefined) ?? NaN;
    L.push('=== MNEMO PROFILE REPORT ===');
    L.push(`gpu: ${env.gpu}`);
    L.push(`drawing buffer ${env.drawingBuffer} (pixel ratio ${fmt(env.pixelRatio, 2)}), three r${env.three}, timing: ${env.timing}, disjoint events ${timer.disjoints}`);
    L.push(`view: camera pitch ${fmt(pitchDeg, 0)} deg (negative = down), yaw ${fmt(yawDeg, 0)}, fov ${fmt(camera.fov, 0)}; sun elevation ${Number.isFinite(sunUp) ? fmt(Math.asin(Math.min(1, sunUp)) * 180 / Math.PI, 0) + ' deg' : 'n/a'}`);
    try { for (const x of ctx.extra?.() ?? []) L.push(x); } catch { /* the extra lines are optional */ }
    L.push(`run took ${Math.round((performance.now() - t0) / 1000)} s${quick ? ' (quick mode: fewer samples)' : ''}`);
    for (const e of errs) L.push(`ERROR in ${e}`);

    const f = res.frame, fl = res.fill, g = res.groups, sk = res.sky, sc = res.scene;
    if (f) {
      L.push('', '--- FRAME (per-frame, over ' + f.num.frames + ' frames) ---');
      L.push(`interval median ${fmt(f.num.gap, 1)} ms (${fmt(1000 / f.num.gap, 0)} fps); CPU frame loop mean ${fmt(f.num.cpuMean)} p90 ${fmt(f.num.cpuP90)} max ${fmt(f.num.cpuMax)}; ${gpu ? `GPU mean ${fmt(f.num.gpuMean)} p90 ${fmt(f.num.gpuP90)}` : 'no GPU timer'}`);
      L.push(`draw calls ${fmt(f.num.calls, 0)}, triangles ${fmt(f.num.tris, 0)}; shadow cascades redrawn in ${f.num.shadowFrames} of ${f.num.frames} frames`);
      const secs = [...f.sections].filter((s: any) => s.gpuMean >= 0.05 || s.cpuMean >= 0.2).sort((a: any, b: any) => Math.max(b.gpuMean || 0, b.cpuMean) - Math.max(a.gpuMean || 0, a.cpuMean)).slice(0, 14);
      table(['section', gpu ? 'GPU mean (median)' : '', 'CPU mean (median)'], secs.map((s: any) => [s.section, gpu ? `${fmt(s.gpuMean)} (${fmt(s.gpuMed)})` : '', `${fmt(s.cpuMean)} (${fmt(s.cpuMed)})`]));
      L.push('worst frames: ' + f.worst.map((w: any) => `${w.frame} ${w['worst of GPU/CPU ms']} ms [${w['biggest sections']}]`).join(' | '));
    }
    if (fl) {
      L.push('', '--- RESOLUTION SCALING (ms at each size; pixel-scaled share is the straight-line fit) ---');
      const head = ['section', ...fl.mpx.map((m: number) => fmt(m, 2) + ' Mpx'), 'fixed ms', 'ms/Mpx', 'scales'];
      const rows = fl.series.filter((s: any) => Math.max(...s.ms) >= 0.2).map((s: any) => [s.label, ...s.ms.map((v: number) => fmt(v)), fmt(Math.max(0, s.fit.a)), fmt(s.fit.b), s.ms[0] > 0 ? fmt(Math.min(100, Math.max(0, 100 * s.fit.b * fl.mpx[0] / s.ms[0])), 0) + '%' : 'n/a']);
      table(head, rows);
      if (Math.abs(fl.driftRatio - 1) > 0.1) L.push(`warning: the first size read ${fmt(100 * (fl.driftRatio - 1), 0)}% different when repeated at the end (clock or scene drift)`);
    }
    const vrs: any[] = res.variants ?? [];
    for (const vr of vrs) {
      const bm = vr.metrics.map((_: string, i: number) => mean(vr.baseline.map((b: number[]) => b[i])));
      L.push('', `--- ${vr.title.toUpperCase()}: GPU ms SAVED PER FRAME BY EACH CHANGE (in the live scene; ${vr.frames} frames each, baseline measured before and after every change; baseline ${vr.metrics.map((m: string, i: number) => `${m} ${fmt(bm[i])} ms`).join(', ')}) ---`);
      table(['change', ...vr.metrics], vr.rows.map((r: any) => [r.name, ...r.saved.map((x: number) => signed(x))]));
    }
    const dr = res.draws;
    if (dr) {
      L.push('', `--- GPU TIME PER DRAW GROUP (each draw timed alone; sum of all draws ${dr.frameSumMs.map((v: number) => fmt(v)).join(' / ')} ms at ${dr.mpx.map((m: number) => fmt(m, 2) + ' Mpx').join(' / ')}; overlap is lost, so it runs a little over the real frame) ---`);
      table(['what ("render" = colour pass, "shadow map" = cascades)', 'draws', 'tris', ...dr.mpx.map((m: number) => `ms@${fmt(m, 2)}Mpx`), 'fixed ms', 'scales'],
        dr.rows.slice(0, 20).map((r: any) => [r.what, fmt(r.draws, 0), fmt(r.tris, 0), ...r.ms.map((v: number) => fmt(v)), fmt(r.fixed), fmt(r.scales, 0) + '%']));
    }
    if (g) {
      L.push('', `--- SUBSYSTEM COSTS (frame ms saved when switched off; ${g.reps} A/B pairs of ${g.frames} frames; baseline ${fmt(g.baseline.gpu)} ms ${gpu ? 'GPU' : 'synced'}, CPU ${fmt(g.baseline.cpu)} ms; noise floor ~${fmt(g.noiseGpuMs)} ms) ---`);
      const rows = [...g.rows].sort((a: any, b: any) => (b.gpu.v || 0) - (a.gpu.v || 0)).map((r: any) => {
        const ctl = r.group.startsWith('(control');
        const sig = ctl ? '' : Math.abs(r.gpu.v) > Math.max(2 * r.gpu.se, g.noiseGpuMs) ? '' : '  within noise';
        return [r.group, `${signed(r.gpu.v)} ± ${fmt(r.gpu.se)}${sig}`, `${signed(r.cpu.v)} ± ${fmt(r.cpu.se)}`, fmt(100 * r.pixChanged, 1) + '%'];
      });
      table(['group', gpu ? 'GPU ms saved' : 'frame ms saved', 'CPU ms saved', 'pixels changed'], rows);
    }
    if (sk) {
      const views: string[] = [...new Set<string>(sk.rows.map((r: any) => r.view))];
      const names: string[] = [...new Set<string>(sk.rows.map((r: any) => r.variant))];
      L.push('', `--- SKY DOME ALONE (isolated, ${sk.env.drawnAt}, best of ${sk.passes ?? 2} passes; ? = passes disagreed by more than 25%) ---`);
      table(['variant', ...views], names.map(n => [n, ...views.map(v => { const r = sk.rows.find((x: any) => x.variant === n && x.view === v); return r ? fmt(r.ms) + (r.spread > 0.25 ? ' ?' : '') : ''; })]));
    }
    if (sc) {
      L.push('', '--- SCENE ---');
      for (const [k, v] of Object.entries(sc.summary)) L.push(`${k}: ${v}`);
      table(['object', 'draws', 'instances', 'triangles', 'caster tris'], sc.rows.slice(0, 8).map((r: any) => [String(r.object), String(r['draw calls']), String(r.instances), String(r.triangles), String(r['shadow-caster tris'])]));
    }

    // ---- findings, worked out here so they do not have to be read off the tables ----
    const F: string[] = [];
    if (f) {
      F.push(f.verdict);
      const secs = [...f.sections].filter((s: any) => s.section !== 'other');
      if (gpu) {
        const tops = secs.sort((a: any, b: any) => b.gpuMean - a.gpuMean).slice(0, 3).filter((s: any) => s.gpuMean > 0.3);
        if (tops.length) F.push('GPU time is mostly: ' + tops.map((s: any) => `${s.section} ${fmt(s.gpuMean, 1)} ms (${fmt(100 * s.gpuMean / f.num.gpuMean, 0)}%)`).join(', ') + '.');
      }
      // While the GPU is saturated its command queue is full and the CPU waits inside whichever call finds it full (a texture upload, a uniform write, the render
      // call): the frame loop's CPU time then contains GPU waiting and is no limit of its own. A section whose mean is far above its median is a few stalled
      // frames, not a steady cost; ranking by the mean alone points at the wrong code.
      const gpuSat = gpu && f.num.gap > 0 && f.num.gpuMean > 0.85 * f.num.gap;
      if (f.num.cpuP90 > 16.7) {
        const top = secs.sort((a: any, b: any) => b.cpuMed - a.cpuMed || b.cpuMean - a.cpuMean).slice(0, 3).map((s: any) => `${s.section} median ${fmt(s.cpuMed, 1)}, mean ${fmt(s.cpuMean, 1)}, p90 ${fmt(s.cpuP90, 1)}`).join('; ');
        F.push(gpuSat
          ? `CPU p90 ${fmt(f.num.cpuP90, 1)} ms looks over a 60 fps budget, but the GPU is saturated (${fmt(100 * f.num.gpuMean / f.num.gap, 0)}% busy), so much of it is the CPU waiting on a full GPU queue; measure it again after the GPU cost is down. Steady CPU sections (by median): ${top}.`
          : `CPU p90 ${fmt(f.num.cpuP90, 1)} ms is over a 60 fps budget (16.7 ms), so fixing the GPU alone will not reach 60 fps. Biggest CPU sections (by median): ${top}.`);
      }
      const spiky = secs.filter((s: any) => s.cpuMean > 1.5 && s.cpuMean > 4 * Math.max(s.cpuMed, 0.05)).sort((a: any, b: any) => b.cpuMean - a.cpuMean).slice(0, 3);
      if (spiky.length) F.push('Spiky CPU sections (a few frames carry the mean; look at the worst frames): ' + spiky.map((s: any) => `${s.section} median ${fmt(s.cpuMed, 1)}, mean ${fmt(s.cpuMean, 1)}, p90 ${fmt(s.cpuP90, 1)}, max ${fmt(s.cpuMax, 1)}`).join('; ') + '.');
      if (f.num.shadowFrames > 0.5 * f.num.frames) F.push(`Shadow cascades are redrawn in ${f.num.shadowFrames} of ${f.num.frames} frames, so they are not on demand in this state.`);
    }
    if (fl) {
      const s0 = fl.series[0], share = s0.ms[0] > 0 ? 100 * s0.fit.b * fl.mpx[0] / s0.ms[0] : NaN;
      if (Number.isFinite(share)) F.push(share > 60 ? `Fill-bound: ${fmt(share, 0)}% of the frame scales with pixel count (fixed part ${fmt(Math.max(0, s0.fit.a), 1)} ms), so resolution and per-pixel shader cost are the levers.` : `Not mainly fill-bound: only ${fmt(share, 0)}% of the frame scales with pixel count; the cost is vertices, draw calls, passes or the CPU.`);
    }
    for (const vr of vrs) {
      vr.metrics.forEach((mn: string, mi: number) => {
        const best = [...vr.rows].sort((a: any, b: any) => b.saved[mi] - a.saved[mi]).slice(0, 4).filter((r: any) => r.saved[mi] > 0.3);
        if (best.length) F.push(`${vr.title}: biggest savings in ${mn}: ` + best.map((r: any) => `${r.name} ${signed(r.saved[mi], 1)} ms`).join('; ') + '.');
      });
    }
    if (dr && dr.rows.length) {
      const tot = dr.frameSumMs[0];
      // Do the per-draw times add up to the frame? Each draw is timed alone, so the sum should be a little OVER the colour pass. If it is far under, the per-draw
      // figures read low on this GPU (a long fragment-bound draw inside a query of its own is the usual case) and are a lower bound, not a share of the frame.
      const renderSec = f?.sections.find((s: any) => s.section === 'render');
      const colourSum = sum(dr.rows.filter((r: any) => r.what === 'render' || (r.what.startsWith('render > ') && !r.what.startsWith('render > shadow map'))).map((r: any) => r.ms[0]));
      if (renderSec && gpu && renderSec.gpuMean > 4 && colourSum < 0.75 * renderSec.gpuMean) {
        F.push(`The per-draw times explain only ${fmt(100 * colourSum / renderSec.gpuMean, 0)}% of the colour pass (the draws add up to ${fmt(colourSum, 1)} ms; 'render' in the frame run is ${fmt(renderSec.gpuMean, 1)} ms): per-draw GPU times read low on this GPU, so every figure below taken from them (and every variant metric read per draw) is a LOWER BOUND, and a share is a share of the draws' sum, not of the frame. Trust whole-frame and section figures (RESOLUTION SCALING, the cloud volume variants, 'cloud volume (small)').`);
      }
      F.push('Heaviest draw groups (timed alone, in the colour pass and shadow passes; % is of the draws\' sum): ' + dr.rows.slice(0, 6).map((r: any) => `${r.what} ${fmt(r.ms[0], 1)} ms (${fmt(100 * r.ms[0] / tot, 0)}%, ${fmt(r.fixed, 1)} fixed + ${fmt(r.scales, 0)}% scaling with pixels)`).join('; ') + '.');
      const sh = dr.rows.filter((r: any) => r.what.includes('shadow map')).reduce((a: number, r: any) => a + r.ms[0], 0);
      if (sh > 0) F.push(`All shadow-pass draws together: ${fmt(sh, 1)} ms of ${fmt(tot, 1)} ms.`);
      const geo = dr.rows.filter((r: any) => r.scales < 35 && r.fixed > 0.8).slice(0, 4);
      if (geo.length) F.push('Mostly vertex or draw-call bound (lowering resolution will not help): ' + geo.map((r: any) => `${r.what} ${fmt(r.fixed, 1)} ms`).join('; ') + '.');
    }
    if (g) {
      const ctl = g.rows.find((r: any) => r.group.startsWith('(control')), real = g.rows.filter((r: any) => r !== ctl);
      const solid = real.filter((r: any) => r.gpu.v > Math.max(2 * r.gpu.se, g.noiseGpuMs)).sort((a: any, b: any) => b.gpu.v - a.gpu.v);
      if (solid.length) F.push('Measured subsystem costs: ' + solid.slice(0, 5).map((r: any) => `${r.group} ${fmt(r.gpu.v, 1)} ms ± ${fmt(r.gpu.se, 1)}`).join('; ') + '.');
      const vague = real.filter((r: any) => r.gpu.se > 2).map((r: any) => r.group);
      if (vague.length) F.push('Error bars over 2 ms (treat as unresolved, usually the GPU changing clock): ' + vague.join(', ') + '.');
      const quiet = new Set([...builtIn, ...(ctx.groups ?? [])].filter(x => x.noPixels).map(x => x.name));
      const steady = ctl && ctl.pixChanged < 0.05;      // if the picture moves by itself (sea, grass, cloud), a switch changing nothing cannot be told from motion
      const useless = steady ? real.filter((r: any) => r.gpu.v > 0.3 && !quiet.has(r.group) && r.pixChanged <= ctl.pixChanged + 0.003)
        .map((r: any) => `${r.group} (${fmt(r.gpu.v, 1)} ms, changes ${fmt(100 * r.pixChanged, 1)}% of pixels vs ${fmt(100 * ctl.pixChanged, 1)}% with nothing switched)`) : [];
      if (ctl && !steady) F.push(`The picture changes ${fmt(100 * ctl.pixChanged, 0)}% by itself between samples here, so "pixels changed" cannot show what a switch removes in this view.`);
      if (useless.length) F.push('Costs time but changes nothing visible in this view, candidates to remove or cull: ' + useless.join('; ') + '.');
    }
    if (sk) {
      const val = (n: string, v: string): number => (sk.rows.find((r: any) => r.variant === n && r.view === v) ?? { ms: NaN }).ms;
      const views: string[] = [...new Set<string>(sk.rows.map((r: any) => r.view))];
      F.push(`Sky pass alone: ${views.map(v => `${v} ${fmt(val('sky pass', v), 2)} ms`).join(', ')}, against ${views.map(v => `${fmt(val('fill floor', v), 2)} ms`).join(', ')} for a plain fill of the screen.`);
    }
    if (errs.length) F.push(`${errs.length} stage(s) failed, see ERROR lines.`);
    L.splice(5 + errs.length, 0, '', '--- FINDINGS (worked out by the profiler) ---', ...F.map(x => '* ' + x));

    lastReport = L.join('\n');
    const copied = await copyText(lastReport);
    ensurePanel(); boxText!.value = lastReport;
    boxStatus!.textContent = copied ? `Done in ${Math.round((performance.now() - t0) / 1000)} s. The report is on your clipboard: paste it into the chat. (Or use Copy below.)` : `Done in ${Math.round((performance.now() - t0) / 1000)} s. Click Copy, then paste it into the chat.`;
    console.log(lastReport);
    return copied ? 'report copied to the clipboard' : 'report ready: click Copy in the panel (bottom right)';
  }

  const api = {
    report,
    get lastReport() { return lastReport; },
    toggle,
    frame: frameRun,
    draws: drawsRun,
    variants: variantsRun,
    groups: groupsRun,
    fill: fillRun,
    scene: sceneAudit,
    async sky(o?: SkyRunOptions) { const r = await sky_(o); last = { ...last, sky: r }; return r; },
    async all(o: { sky?: boolean } = {}) {
      const frame = await frameRun(); const fill = await fillRun(); const groups = await groupsRun(); const scene = sceneAudit();
      return o.sky ? { frame, fill, groups, scene, sky: await api.sky() } : { frame, fill, groups, scene };
    },
    json() { return JSON.stringify(last, null, 1); },
    get last() { return last; },
    groupNames: [...builtIn, ...(ctx.groups ?? [])].map(g => g.name),
    skyVariants: SKY_VARIANT_NOTES,
    help() {
      console.log([
        'await prof.report()                 EVERYTHING in one go (2 to 3 minutes): runs all of the below and copies one text report to the clipboard\n' +
        '     (or shows it in a panel with a Copy button). Paste that.\n' +
        'await prof.frame({frames: 120})     where a frame goes: every section of the frame loop, GPU and CPU, plus the worst frames and a verdict',
        'await prof.groups({frames: 20, reps: 3, only: ["grass"]})   what each subsystem costs, switched off and on in the live scene (A B A B), with a noise control',
        '     groups: ' + [...builtIn, ...(ctx.groups ?? [])].map(g => g.name).join(', '),
        'prof.toggle("ground plane")        switch a group off (call again to switch it on), to look at the result; prof.toggle() lists the states. groups() and fill() put all groups back on',
        'await prof.draws()                 GPU ms of every draw call, grouped by what it draws, in the colour pass and the shadow passes, at two resolutions\n' +
        'await prof.fill({ratios: [1, .75, .5, .35]})   how much of the frame scales with pixel count (fill-bound or not), per section',
        'await prof.variants({only: ["cloud"]})   cheaper versions of one thing applied one at a time in the live scene, GPU ms saved each (sets: ' + variantSets().map(s => s.title).join('; ') + ')',
        'prof.scene()                        draw calls, triangles and instances per object, lights, shader programs',
        'await prof.sky({only: ["full"], views: ["current"]})   the sky pass alone, against a plain fill of the screen (see prof.skyVariants)',
        'await prof.all({sky: false})        frame, fill, groups, scene          prof.json()  the last results as text',
        'Measure in the state you care about (look where it is slow, sun where it is slow). Keep the tab visible. The adaptive quality controller is held while a measurement runs.',
        'GPU timer: ' + (gpu ? 'available' : 'NOT available in this browser: the GPU columns are blank and groups() and fill() use synced frame time'),
      ].join('\n'));
    },
  };
  (window as any).prof = api;

  return {
    begin, mark, end,
    dispose() {
      cap = null; abortFrame();
      sm.render = origShadowRender;
      (renderer as any).renderBufferDirect = rbd;
      for (const g of builtIn) { try { g.on(); } catch { /* the objects may be gone */ } }
      box?.remove(); box = null;
      window.removeEventListener('error', onErr); window.removeEventListener('unhandledrejection', onRej);
      if ((window as any).prof === api) delete (window as any).prof;
    },
  };
}

